import { useRef, useState, useEffect } from 'react';

export default function VideoHero() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.play().catch(() => {});
  }, []);

  const scrollToContent = () => {
    const el = document.getElementById('benefits');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative w-full h-screen min-h-[600px] overflow-hidden bg-black" style={{ height: '100svh' }}>
      {/* ── Mobile background image (portrait-optimised, shown only on small screens) ── */}
      <div
        className="sm:hidden absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url("https://public.readdy.ai/ai/img_res/eb81f1b02d6efc7fe55044e67869789f.jpg")`,
        }}
      />

      {/* ── Video background (hidden on mobile, shown sm+) ── */}
      <video
        ref={videoRef}
        className="hidden sm:block absolute inset-0 w-full h-full object-cover"
        muted
        playsInline
        autoPlay
        loop
        preload="auto"
        poster="https://readdy.ai/api/search-image?query=Cinematic%20wide-angle%20interior%20of%20a%20prestigious%20law%20firm%20conference%20room%20with%20dark%20polished%20wood%20table%2C%20tall%20leather%20chairs%2C%20floor%20to%20ceiling%20windows%20revealing%20a%20dramatic%20city%20skyline%20at%20dusk%2C%20warm%20amber%20lighting%2C%20golden%20hour%20glow%2C%20legal%20books%20on%20shelves%2C%20sophisticated%20and%20powerful%20atmosphere%2C%20photorealistic%20architectural%20photography%2C%20deep%20shadows%20and%20rich%20tones&width=1920&height=1080&seq=lawfirm-hero-poster-v5&orientation=landscape"
      >
        <source src="https://videos.pexels.com/video-files/7675988/7675988-uhd_2560_1440_30fps.mp4" type="video/mp4" />
        <source src="https://videos.pexels.com/video-files/6077368/6077368-hd_1920_1080_25fps.mp4" type="video/mp4" />
        <source src="https://videos.pexels.com/video-files/8386440/8386440-uhd_2560_1440_25fps.mp4" type="video/mp4" />
      </video>

      {/* ── Layered overlays for depth ── */}
      <div className="absolute inset-0 bg-black/55 pointer-events-none" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.1) 40%, rgba(0,0,0,0.7) 100%)',
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 35%, rgba(0,0,0,0.5) 100%)',
        }}
      />

      {/* ── Hero content ── */}
      <div className="absolute inset-0 flex flex-col items-center justify-center z-10 px-6 text-center">

        {/* Main headline */}
        <div
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 1s ease 0.35s, transform 1s ease 0.35s',
          }}
        >
          <h1
            className="text-3xl sm:text-5xl md:text-7xl lg:text-[5.5rem] font-bold text-white leading-[1.05] mb-4 tracking-tight"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Generate More Personal Injury
          </h1>
          <h1
            className="text-3xl sm:text-5xl md:text-7xl lg:text-[5.5rem] font-bold leading-[1.05] mb-8 tracking-tight"
            style={{
              fontFamily: 'Playfair Display, serif',
              WebkitTextStroke: '1px #D4AF37',
              color: 'transparent',
            }}
          >
            Cases For Your Firm
          </h1>
        </div>

        {/* Subtitle */}
        <div
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.9s ease 0.6s, transform 0.9s ease 0.6s',
          }}
        >
          <p
            className="text-sm md:text-base lg:text-lg text-white/70 max-w-xl leading-relaxed mb-10 tracking-wide px-2 md:px-0"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Premium Google Ads lead generation for elite personal injury law firms
          </p>
        </div>

        {/* CTA buttons */}
        <div
          className="flex flex-col sm:flex-row gap-4 items-center"
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.9s ease 0.85s, transform 0.9s ease 0.85s',
          }}
        >
          <button
            onClick={() => {
              const el = document.getElementById('consultation');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="px-10 py-4 bg-[#D4AF37] text-black text-sm font-bold rounded-md hover:bg-[#e8c547] transition-all duration-300 whitespace-nowrap cursor-pointer tracking-widest uppercase"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Book a Consultation
          </button>
        </div>
      </div>

      {/* ── Scroll indicator ── */}
      <div
        className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3 cursor-pointer"
        onClick={scrollToContent}
        style={{
          opacity: mounted ? 1 : 0,
          transition: 'opacity 1s ease 1.3s',
        }}
      >
        <span
          className="text-white/40 text-[10px] tracking-[0.3em] uppercase"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          Scroll
        </span>
        <div className="w-px h-12 bg-gradient-to-b from-white/40 to-transparent" />
      </div>
    </section>
  );
}
